/** 選んだ機能の詳細（右）。設定とプロンプトをタブで切り替える */

import { ChevronDown } from 'lucide-react'

import type { AiModelsResponse, AiPrompt, AiUsageItem } from '@/api/types/ai-usages'
import { Badge } from '@/components/ui/badge'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { parseUtcDate } from '@/lib/date-utils'

import { AiUsageEditForm } from './ai-usage-edit-form'
import { KIND_LABELS } from './ai-usage-utils'

const PromptBlock = ({ prompt, defaultOpen }: { prompt: AiPrompt; defaultOpen: boolean }) => (
  <Collapsible defaultOpen={defaultOpen} className="rounded-md border">
    <CollapsibleTrigger className="group flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-accent/50">
      <span className="min-w-0">
        <span className="block font-medium">{prompt.label}</span>
        <span className="block truncate font-mono text-xs text-muted-foreground">{prompt.target}</span>
      </span>
      <ChevronDown size={16} className="shrink-0 transition-transform group-data-[state=open]:rotate-180" />
    </CollapsibleTrigger>
    <CollapsibleContent>
      {prompt.text === null ? (
        <p className="px-3 pb-3 text-xs text-destructive">プロンプトを読み込めませんでした</p>
      ) : (
        <pre className="max-h-[28rem] overflow-auto border-t bg-muted/50 p-3 text-xs whitespace-pre-wrap">
          {prompt.text}
        </pre>
      )}
    </CollapsibleContent>
  </Collapsible>
)

const PaidSummary = ({ item }: { item: AiUsageItem }) => {
  if (!item.allow_paid) {
    return <p className="text-xs text-muted-foreground">有料のキーには切り替えません（無料のキーだけで答えます）。</p>
  }
  return (
    <div className="rounded-md border bg-muted/30 px-3 py-2 text-sm">
      <p className="text-xs font-medium text-muted-foreground">有料のキーで答えた回数</p>
      <p className="mt-0.5 tabular-nums">
        <span className={item.paid_today > 0 ? 'font-semibold text-amber-600 dark:text-amber-400' : ''}>
          今日 {item.paid_today} 回
        </span>
        <span className="ml-3 text-muted-foreground">直近 7 日 {item.paid_last_7_days} 回</span>
      </p>
      {item.paid_last_at && (
        <p className="text-xs text-muted-foreground">最後: {parseUtcDate(item.paid_last_at).toLocaleString('ja-JP')}</p>
      )}
    </div>
  )
}

const ReadOnlySettings = ({ item }: { item: AiUsageItem }) => (
  <div className="space-y-2 text-sm">
    <p className="font-mono">{item.models.map((model) => model.name).join(' → ')}</p>
    <p className="text-xs text-muted-foreground">
      埋め込みはモデルを変えるとベクトルを作り直すことになるため、ここからは変えられません。
    </p>
  </div>
)

export const AiUsageDetail = ({
  item,
  catalog,
  onSaved,
}: {
  item: AiUsageItem
  catalog: AiModelsResponse | null
  onSaved: (item: AiUsageItem) => void
}) => (
  <div className="space-y-4">
    <div>
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-lg font-semibold">{item.label}</h2>
        <Badge variant="outline">{KIND_LABELS[item.kind]}</Badge>
        {item.customized && (
          <Badge variant="outline" className="border-amber-500/50 text-amber-600 dark:text-amber-400">
            変更済み
          </Badge>
        )}
      </div>
      <p className="mt-1 text-sm text-muted-foreground">{item.description}</p>
      <p className="mt-1 break-all font-mono text-xs text-muted-foreground">{item.source}</p>
    </div>

    <PaidSummary item={item} />

    <Tabs defaultValue="settings">
      <TabsList className="w-full">
        <TabsTrigger value="settings">設定</TabsTrigger>
        <TabsTrigger value="prompts">プロンプト（読み取り専用）</TabsTrigger>
      </TabsList>
      <TabsContent value="settings" className="pt-2">
        {item.editable ? (
          // 保存・切り替えのたびに入力を作り直し、開いたときの値を基準にする
          <AiUsageEditForm
            key={`${item.key}-${JSON.stringify(item.models)}-${item.allow_paid}-${item.quota_guard}-${JSON.stringify(item.paid_models)}-${JSON.stringify(item.profiles)}`}
            item={item}
            catalog={catalog}
            onSaved={onSaved}
          />
        ) : (
          <ReadOnlySettings item={item} />
        )}
      </TabsContent>
      <TabsContent value="prompts" className="space-y-2 pt-2">
        {item.prompt_note && <p className="text-sm text-muted-foreground">{item.prompt_note}</p>}
        {item.prompts.length === 0 && !item.prompt_note && (
          <p className="text-sm text-muted-foreground">
            この機能にはプロンプトがありません（ベクトルを作るだけです）。
          </p>
        )}
        {item.prompts.map((prompt, index) => (
          <PromptBlock key={prompt.target} prompt={prompt} defaultOpen={index === 0} />
        ))}
      </TabsContent>
    </Tabs>
  </div>
)
